import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Tab,
  Tabs,
  Typography,
  TextField,
  Alert,
  Stack,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Link,
  IconButton,
  Tooltip,
} from '@mui/material';
import OpenInNew from '@mui/icons-material/OpenInNew';
import ShowChart from '@mui/icons-material/ShowChart';
import Sync from '@mui/icons-material/Sync';
import AccountBalance from '@mui/icons-material/AccountBalance';
import PieChartIcon from '@mui/icons-material/PieChart';
import Lightbulb from '@mui/icons-material/Lightbulb';
import Logout from '@mui/icons-material/Logout';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip as ReTooltip,
  Legend,
} from 'recharts';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  getBreezeStatus,
  exchangeBreezeSession,
  clearBreezeSession,
  syncBreezePortfolio,
  getBreezePortfolioLatest,
  getBreezeCollateralAnalysis,
  getBreezeOpportunities,
  getBreezeDematHoldings,
  getBreezeHoldingsPlInsights,
  parseApiSession,
  pickApiSessionFromSearchParams,
  persistApisessionHandover,
  clearStoredApisession,
  getStoredApisession,
  BreezeStatus,
} from '../../../services/request/BreezeWealth';
import { formatCurrency } from '../../../utils/CurrencyFormat';
import BreezeChartAnalysisModal from './BreezeChartAnalysisModal';
import BreezePortfolioKpiPanel from './BreezePortfolioKpiPanel';

const PIE_COLORS = ['#1565c0', '#2e7d32', '#ef6c00', '#6a1b9a', '#00838f', '#5d4037'];

function breezeApiErrorMessage(e: unknown): string {
  const ax = e && typeof e === 'object' && 'response' in e ? (e as { response?: { data?: unknown } }).response : undefined;
  const data = ax?.data;
  if (typeof data === 'string') return data;
  if (data && typeof data === 'object' && data !== null) {
    const m = (data as { message?: unknown }).message;
    if (typeof m === 'string' && m.length > 0) return m;
    const err = (data as { error?: unknown }).error;
    if (typeof err === 'string' && err.length > 0) return err;
  }
  return '';
}

const BreezeWealthHub: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(0);
  const [status, setStatus] = useState<BreezeStatus | null>(null);
  const [sessionInput, setSessionInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [portfolioJson, setPortfolioJson] = useState<unknown>(null);
  const [collateral, setCollateral] = useState<Record<string, unknown> | null>(null);
  const [opportunities, setOpportunities] = useState<Record<string, unknown> | null>(null);
  /** Live GET /dematholdings response (same schema as ICICI docs). */
  const [dematLive, setDematLive] = useState<Record<string, unknown> | null>(null);
  /** GET /insights/holdings-pl — P/L by stock (NSE+BSE). */
  const [holdingsPl, setHoldingsPl] = useState<Record<string, unknown> | null>(null);
  /** Bumps when sessionStorage handover token changes so we re-read it. */
  const [handoverRev, setHandoverRev] = useState(0);
  /** Historical chart + pattern hints modal */
  const [chartDlg, setChartDlg] = useState<{ open: boolean; stock?: string; exchange?: string }>({ open: false });

  const hasHandoverInTab = useMemo(() => !!getStoredApisession()?.trim(), [handoverRev]);

  const loadStatus = useCallback(async () => {
    try {
      const res = await getBreezeStatus();
      const d = res.data as BreezeStatus;
      setStatus(d);
    } catch {
      setStatus(null);
    }
  }, []);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    setHandoverRev((r) => r + 1);
  }, []);

  useEffect(() => {
    const connected = searchParams.get('connected');
    const oauthErr = searchParams.get('oauth_error');
    const oauthMiss = searchParams.get('oauth');
    if (connected !== '1' && oauthErr !== '1' && oauthMiss !== 'missing') return;

    const onceKey = `breeze_hub_oauth_${searchParams.toString()}`;
    if (sessionStorage.getItem(onceKey)) {
      navigate('/admin/icici-breeze', { replace: true });
      return;
    }
    sessionStorage.setItem(onceKey, '1');

    if (connected === '1') {
      toast.success('ICICI Breeze connected — session token saved on the server.');
    } else if (oauthErr === '1') {
      toast.error('Could not exchange API session. Check Breeze keys on the server and try again.');
    } else {
      toast.warning(
        'No apisession / API_Session in the redirect URL. Register the callback URL in the Breeze portal (e.g. /admin/icici-breeze/oauth).'
      );
    }
    void loadStatus();
    navigate('/admin/icici-breeze', { replace: true });
  }, [searchParams, navigate, loadStatus]);

  /** ICICI returns `?apisession=` after login (e.g. on whatzneu.com). Open the same query on localhost to auto-exchange. */
  useEffect(() => {
    const token = pickApiSessionFromSearchParams(searchParams);
    if (!token) return;

    const doneKey = `breeze_apisession_ok_${token}`;
    if (sessionStorage.getItem(doneKey)) {
      navigate('/admin/icici-breeze', { replace: true });
      return;
    }

    let cancelled = false;
    async function run() {
      try {
        setLoading(true);
        let decoded = token;
        try {
          decoded = decodeURIComponent(token.replace(/\+/g, '%20'));
        } catch {
          decoded = token;
        }
        persistApisessionHandover(decoded);
        setHandoverRev((r) => r + 1);
        await exchangeBreezeSession(decoded);
        if (cancelled) return;
        sessionStorage.setItem(doneKey, '1');
        toast.success(
          'apisession exchanged — the server stores the Breeze session token and uses it for portfolio and analysis calls.'
        );
        await loadStatus();
        navigate('/admin/icici-breeze', { replace: true });
      } catch (e: unknown) {
        if (cancelled) return;
        toast.error(
          breezeApiErrorMessage(e) ||
            'Could not exchange apisession. Check keys on lead-service and use a fresh token from login.'
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void run();
    return () => {
      cancelled = true;
    };
  }, [searchParams, navigate, loadStatus]);

  const handleExchange = async () => {
    const raw = parseApiSession(sessionInput);
    if (!raw) {
      toast.error('Paste apisession value, API_Session, or full redirect URL.');
      return;
    }
    try {
      setLoading(true);
      persistApisessionHandover(raw);
      setHandoverRev((r) => r + 1);
      await exchangeBreezeSession(raw);
      toast.success('Session token saved on server.');
      setSessionInput('');
      await loadStatus();
    } catch (e: unknown) {
      toast.error(
        breezeApiErrorMessage(e) || 'Exchange failed. Check App Key / Secret on lead-service and use a fresh apisession.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    try {
      setLoading(true);
      const res = await syncBreezePortfolio();
      setPortfolioJson(res.data);
      toast.success('Portfolio synced.');
      await loadCollateralAndOpps();
    } catch (e: unknown) {
      toast.error('Sync failed — ensure session is valid.');
    } finally {
      setLoading(false);
    }
  };

  const loadLatest = async () => {
    try {
      setLoading(true);
      const res = await getBreezePortfolioLatest();
      setPortfolioJson(res.data);
    } catch {
      toast.error('Could not load snapshot.');
    } finally {
      setLoading(false);
    }
  };

  const loadCollateralAndOpps = async () => {
    try {
      const [c, o] = await Promise.all([getBreezeCollateralAnalysis(), getBreezeOpportunities()]);
      setCollateral(c.data as Record<string, unknown>);
      setOpportunities(o.data as Record<string, unknown>);
    } catch {
      /* optional */
    }
  };

  useEffect(() => {
    if (tab === 1 || tab === 2 || tab === 3) {
      void loadLatest();
      void loadCollateralAndOpps();
    }
  }, [tab]);

  const dematRows = (): Array<Record<string, string>> => {
    try {
      const root = portfolioJson as Record<string, unknown> | null;
      const dh = root?.dematholdings as Record<string, unknown> | undefined;
      const success = dh?.Success;
      if (Array.isArray(success)) {
        return success as Array<Record<string, string>>;
      }
    } catch {
      /* ignore */
    }
    return [];
  };

  const handleFetchLiveDemat = async () => {
    try {
      setLoading(true);
      const res = await getBreezeDematHoldings();
      setDematLive(res.data as Record<string, unknown>);
      toast.success('Demat holdings loaded from ICICI.');
    } catch (e: unknown) {
      setDematLive(null);
      toast.error(breezeApiErrorMessage(e) || 'Failed to fetch dematholdings. Exchange session on Connect first.');
    } finally {
      setLoading(false);
    }
  };

  const handleLoadHoldingsPl = async () => {
    try {
      setLoading(true);
      const res = await getBreezeHoldingsPlInsights();
      setHoldingsPl(res.data as Record<string, unknown>);
      toast.success('Holdings P/L loaded (NSE + BSE portfolio).');
    } catch (e: unknown) {
      setHoldingsPl(null);
      toast.error(breezeApiErrorMessage(e) || 'Could not load holdings P/L. Connect and exchange session first.');
    } finally {
      setLoading(false);
    }
  };

  const dematLiveRows = (): Array<Record<string, string>> => {
    const s = dematLive?.Success;
    if (!Array.isArray(s)) return [];
    return s as Array<Record<string, string>>;
  };

  const dematLiveColumnKeys = (): string[] => {
    const rows = dematLiveRows();
    if (rows.length === 0) return [];
    const keys = new Set<string>();
    rows.forEach((r) => Object.keys(r).forEach((k) => keys.add(k)));
    return Array.from(keys).sort();
  };

  const pieData = (): { name: string; value: number }[] => {
    const segs = collateral?.segments as Array<Record<string, unknown>> | undefined;
    if (!Array.isArray(segs)) return [];
    return segs
      .map((s) => ({
        name: String(s.label ?? '—'),
        value: Number(s.amount ?? 0),
      }))
      .filter((x) => x.value > 0);
  };

  return (
    <Box sx={{ p: 2, maxWidth: 1200, mx: 'auto' }}>
      <ToastContainer position="top-right" autoClose={3200} />
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <AccountBalance color="primary" sx={{ fontSize: 40 }} />
        <Box>
          <Typography variant="h5">ICICI Breeze — Wealth hub</Typography>
          <Typography variant="body2" color="text.secondary">
            OAuth login + server-side signing. Register redirect URL (e.g. https://127.0.0.1:3000/admin/icici-breeze/oauth)
            in the Breeze portal.
          </Typography>
        </Box>
      </Stack>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tab label="Connect" />
        <Tab label="Portfolio sync" />
        <Tab label="Collateral" icon={<PieChartIcon />} iconPosition="start" />
        <Tab label="Opportunities" icon={<Lightbulb />} iconPosition="start" />
      </Tabs>

      {tab === 0 && (
        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Alert severity="info">
                After login, ICICI redirects with <code>?apisession=…</code>; we save that value in{' '}
                <strong>sessionStorage</strong> (this tab) and send it as <code>X-Breeze-Apisession</code> on each call to
                lead-service. The server still exchanges it once for ICICI’s <code>session_token</code> (secrets never run
                in the browser). Set <code>BREEZE_APP_KEY</code> and <code>BREEZE_SECRET_KEY</code> on lead-service (never in
                the browser). ICICI login uses{' '}
                <code>
                  https://api.icicidirect.com/apiuser/login?api_key=…
                </code>{' '}
                and redirects with <code>apisession</code> in the query (e.g.{' '}
                <code>https://whatzneu.com/?apisession=…</code>). For local dev, open{' '}
                <code>
                  http://localhost:3000/admin/icici-breeze?apisession=YOUR_TOKEN
                </code>{' '}
                or register <code>/admin/icici-breeze/oauth</code> as the redirect URL. The server exchanges{' '}
                <code>apisession</code> once via CustomerDetails and then uses the returned <code>session_token</code> for
                all Breeze API calls — the raw apisession is not sent on every request.
              </Alert>
              {status && (
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip
                    label={status.configured ? 'Keys configured' : 'Keys missing'}
                    color={status.configured ? 'success' : 'warning'}
                    size="small"
                  />
                  <Chip
                    label={status.hasSession ? 'Session active' : 'No session'}
                    color={status.hasSession ? 'success' : 'default'}
                    size="small"
                  />
                  {status.cookieSessionLinked && (
                    <Chip label="Browser session linked" color="info" size="small" variant="outlined" />
                  )}
                  {status.exchangedAt && (
                    <Chip label={`Last exchange: ${new Date(status.exchangedAt).toLocaleString()}`} size="small" />
                  )}
                  {hasHandoverInTab && (
                    <Chip label="apisession in tab session" color="secondary" size="small" variant="outlined" />
                  )}
                </Stack>
              )}
              {status?.loginUrl && (
                <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                  <Button
                    variant="contained"
                    onClick={() => {
                      window.location.assign(status.loginUrl!);
                    }}
                  >
                    Log in with ICICI (redirect)
                  </Button>
                  <Button
                    variant="outlined"
                    endIcon={<OpenInNew />}
                    href={status.loginUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open login in new tab
                  </Button>
                </Stack>
              )}
              {status?.docsUrl && (
                <Typography variant="body2">
                  Docs:{' '}
                  <Link href={status.docsUrl} target="_blank" rel="noreferrer">
                    Breeze API reference
                  </Link>
                  {' · '}
                  <Link
                    href="https://www.nseindia.com/regulations/listing-compliance/nse-market-capitalisation-all-companies"
                    target="_blank"
                    rel="noreferrer"
                  >
                    NSE listed companies (reference)
                  </Link>
                </Typography>
              )}
              <TextField
                label="apisession, API_Session, or full redirect URL"
                value={sessionInput}
                onChange={(e) => setSessionInput(e.target.value)}
                fullWidth
                multiline
                minRows={2}
                placeholder="e.g. https://127.0.0.1/?API_Session=...."
              />
              <Stack direction="row" spacing={1}>
                <Button variant="contained" onClick={() => void handleExchange()} disabled={loading}>
                  {loading ? <CircularProgress size={22} /> : 'Exchange & save session'}
                </Button>
                <Button
                  color="warning"
                  startIcon={<Logout />}
                  onClick={async () => {
                    await clearBreezeSession();
                    clearStoredApisession();
                    setHandoverRev((r) => r + 1);
                    toast.info('Server session and tab handover token cleared.');
                    await loadStatus();
                  }}
                >
                  Clear session
                </Button>
                <Button onClick={() => void loadStatus()}>Refresh status</Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      )}

      {tab === 1 && (
        <Card>
          <CardContent>
            <Stack spacing={2}>
              <>
              <Alert severity="info">
                Live demat uses ICICI{' '}
                <Link
                  href="https://api.icicidirect.com/breezeapi/documents/index.html#dematholdings"
                  target="_blank"
                  rel="noreferrer"
                >
                  GET /dematholdings
                </Link>{' '}
                (signed <code>X-Checksum</code>, <code>X-Timestamp</code>, <code>X-AppKey</code>,{' '}
                <code>X-SessionToken</code>, body <code>{'{}'}</code>). Complete session exchange on the Connect tab first.
              </Alert>
              <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                <Button variant="contained" color="secondary" onClick={() => void handleFetchLiveDemat()} disabled={loading}>
                  {loading ? <CircularProgress size={22} /> : 'Fetch live demat holdings'}
                </Button>
                <Button variant="contained" onClick={() => void handleLoadHoldingsPl()} disabled={loading}>
                  {loading ? <CircularProgress size={22} /> : 'Load holdings P/L (avg vs CMP)'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ShowChart />}
                  onClick={() => setChartDlg({ open: true })}
                  disabled={loading}
                >
                  Chart & signals (pick stock)
                </Button>
              </Stack>
              {holdingsPl?.summary && (
                <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                  {(() => {
                    const s = holdingsPl.summary as Record<string, unknown>;
                    return (
                      <>
                        <Chip size="small" color="success" label={`In profit: ${String(s.profitCount ?? '—')}`} />
                        <Chip size="small" color="error" label={`In loss: ${String(s.lossCount ?? '—')}`} />
                        <Chip size="small" label={`Flat: ${String(s.flatCount ?? '—')}`} />
                        <Chip size="small" variant="outlined" label={`Stocks: ${String(s.stockCount ?? '—')}`} />
                        <Typography variant="body2" component="span">
                          Total P/L:{' '}
                          <Box
                            component="span"
                            sx={{
                              color:
                                Number(s.totalPnl) > 0
                                  ? 'success.main'
                                  : Number(s.totalPnl) < 0
                                    ? 'error.main'
                                    : 'text.primary',
                              fontWeight: 600,
                            }}
                          >
                            {formatCurrency(Number(s.totalPnl ?? 0))}
                          </Box>
                          {s.totalPnlPercent != null && (
                            <Box
                              component="span"
                              sx={{
                                ml: 1,
                                color:
                                  Number(s.totalPnlPercent) > 0
                                    ? 'success.main'
                                    : Number(s.totalPnlPercent) < 0
                                      ? 'error.main'
                                      : 'text.secondary',
                                fontWeight: 600,
                              }}
                            >
                              ({Number(s.totalPnlPercent).toFixed(2)}%)
                            </Box>
                          )}
                        </Typography>
                      </>
                    );
                  })()}
                </Stack>
              )}
              {Array.isArray(holdingsPl?.rows) && (holdingsPl!.rows as unknown[]).length > 0 && (
                <>
                  <Typography variant="subtitle2">Portfolio holdings — P/L (green / red)</Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 440 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Exch</TableCell>
                          <TableCell>Stock</TableCell>
                          <TableCell align="right">Qty</TableCell>
                          <TableCell align="right">Avg</TableCell>
                          <TableCell align="right">CMP</TableCell>
                          <TableCell align="right">Invested</TableCell>
                          <TableCell align="right">Current</TableCell>
                          <TableCell align="right">P/L</TableCell>
                          <TableCell align="right">P/L %</TableCell>
                          <TableCell align="center">Chart</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(holdingsPl!.rows as Array<Record<string, unknown>>).map((r, ri) => {
                          const side = String(r.side ?? '');
                          const pct = r.pnlPercent;
                          const pctNum = pct == null ? null : Number(pct);
                          const color =
                            side === 'profit' ? 'success.main' : side === 'loss' ? 'error.main' : 'text.secondary';
                          return (
                            <TableRow key={ri}>
                              <TableCell>{String(r.exchange ?? '—')}</TableCell>
                              <TableCell>{String(r.stockCode ?? '—')}</TableCell>
                              <TableCell align="right">{r.quantity != null ? String(r.quantity) : '—'}</TableCell>
                              <TableCell align="right">{r.averagePrice != null ? String(r.averagePrice) : '—'}</TableCell>
                              <TableCell align="right">{r.currentPrice != null ? String(r.currentPrice) : '—'}</TableCell>
                              <TableCell align="right">{formatCurrency(Number(r.investedAmount ?? 0))}</TableCell>
                              <TableCell align="right">{formatCurrency(Number(r.currentAmount ?? 0))}</TableCell>
                              <TableCell align="right" sx={{ color, fontWeight: 600 }}>
                                {formatCurrency(Number(r.pnlAmount ?? 0))}
                              </TableCell>
                              <TableCell align="right" sx={{ color, fontWeight: 600 }}>
                                {pctNum != null && !Number.isNaN(pctNum) ? `${pctNum.toFixed(2)}%` : '—'}
                              </TableCell>
                              <TableCell align="center">
                                <Tooltip title="Historical chart & simple buy/hold/sell-style hints">
                                  <IconButton
                                    size="small"
                                    aria-label="Chart and signals"
                                    onClick={() =>
                                      setChartDlg({
                                        open: true,
                                        stock: String(r.stockCode ?? ''),
                                        exchange: String(r.exchange ?? 'NSE'),
                                      })
                                    }
                                  >
                                    <ShowChart fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {holdingsPl?.disclaimer && (
                    <Typography variant="caption" color="text.secondary">
                      {String(holdingsPl.disclaimer)}
                    </Typography>
                  )}
                </>
              )}
              {holdingsPl && holdingsPl.error && (
                <Alert severity="error">{String(holdingsPl.error)}</Alert>
              )}
              {holdingsPl &&
                !holdingsPl.error &&
                Array.isArray(holdingsPl.rows) &&
                (holdingsPl.rows as unknown[]).length === 0 && (
                  <Alert severity="info">No portfolio holding rows returned (check NSE/BSE responses or session).</Alert>
                )}
              <BreezePortfolioKpiPanel holdingsPl={holdingsPl} />
              {dematLive && (
                <Stack spacing={0.5}>
                  <Typography variant="subtitle2">Live API response</Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Chip size="small" label={`Status: ${String(dematLive.Status ?? '—')}`} />
                    {dematLive.Error != null && String(dematLive.Error) !== 'null' && (
                      <Chip size="small" color="warning" label={`Error: ${String(dematLive.Error)}`} />
                    )}
                  </Stack>
                </Stack>
              )}
              {dematLiveRows().length > 0 && (
                <>
                  <Typography variant="subtitle2">All demat fields (Success array)</Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          {dematLiveColumnKeys().map((k) => (
                            <TableCell key={k}>{k}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {dematLiveRows().map((row, ri) => (
                          <TableRow key={ri}>
                            {dematLiveColumnKeys().map((k) => (
                              <TableCell key={k}>{row[k] != null ? String(row[k]) : '—'}</TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
              {dematLive && dematLiveRows().length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No rows in Success — check Status / Error above or exchange a fresh apisession.
                </Typography>
              )}
              <Button variant="contained" startIcon={<Sync />} onClick={() => void handleSync()} disabled={loading}>
                Sync entire portfolio (Demat + Portfolio + Positions + Funds + Margin)
              </Button>
              <Button variant="outlined" onClick={() => void loadLatest()}>
                Reload last snapshot
              </Button>
              <Typography variant="subtitle2">Demat holdings (from last sync)</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Stock</TableCell>
                      <TableCell align="right">Qty</TableCell>
                      <TableCell>ISIN</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dematRows().length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3}>No rows — sync first.</TableCell>
                      </TableRow>
                    ) : (
                      dematRows().map((r, i) => (
                        <TableRow key={i}>
                          <TableCell>{r.stock_code ?? '—'}</TableCell>
                          <TableCell align="right">{r.quantity ?? '—'}</TableCell>
                          <TableCell>{r.stock_ISIN ?? '—'}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Typography variant="caption" color="text.secondary">
                Raw JSON (advanced)
              </Typography>
              <Paper variant="outlined" sx={{ p: 1, maxHeight: 240, overflow: 'auto', fontFamily: 'monospace', fontSize: 12 }}>
                <pre>{JSON.stringify(portfolioJson, null, 2)}</pre>
              </Paper>
              </>
            </Stack>
          </CardContent>
        </Card>
      )}

      {tab === 2 && (
        <Card sx={{ p: 2 }}>
          <div>
            <>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap">
              <Typography variant="body2" color="text.secondary">
                Live GetFunds + GetMargin (NSE & BSE). Hints flag idle unallocated balance and segment concentration.
              </Typography>
              <Button size="small" variant="outlined" onClick={() => void loadCollateralAndOpps()} disabled={loading}>
                Refresh
              </Button>
            </Stack>
            {pieData().length > 0 ? (
              <Box sx={{ width: '100%', height: 360 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={pieData()} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={120} label>
                      {pieData().map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <ReTooltip formatter={(v: number) => formatCurrency(v)} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <Alert severity="warning">No allocation data — connect Breeze and open this tab (or tap Refresh).</Alert>
            )}
            {collateral?.fundsSummary && typeof collateral.fundsSummary === 'object' && (
              <Paper variant="outlined" sx={{ p: 1.5, mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Funds summary
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {Object.entries(collateral.fundsSummary as Record<string, unknown>).map(([k, v]) => (
                    <Chip key={k} size="small" variant="outlined" label={`${k}: ${String(v)}`} />
                  ))}
                </Stack>
              </Paper>
            )}
            {Array.isArray(collateral?.marginsByExchange) && (
              <Paper variant="outlined" sx={{ p: 1.5, mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Margin by exchange
                </Typography>
                {(collateral!.marginsByExchange as Array<Record<string, unknown>>).map((mx, i) => (
                  <Box key={i} sx={{ mb: 1 }}>
                    <Typography variant="caption" color="primary" display="block">
                      {String(mx.exchange ?? '—')}
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {Object.entries(mx)
                        .filter(([key]) => key !== 'exchange')
                        .map(([k, v]) => (
                          <Chip key={k} size="small" label={`${k}: ${String(v)}`} />
                        ))}
                    </Stack>
                  </Box>
                ))}
              </Paper>
            )}
            {Array.isArray(collateral?.hints) && (collateral!.hints as unknown[]).length > 0 && (
              <Stack spacing={1} sx={{ mt: 2 }}>
                <Typography variant="subtitle2">Collateral hints (avoid idle / misallocated margin)</Typography>
                {(collateral!.hints as Array<Record<string, string>>).map((h, idx) => (
                  <Alert
                    key={idx}
                    severity={h.severity === 'warning' ? 'warning' : h.severity === 'error' ? 'error' : 'info'}
                  >
                    <Typography variant="subtitle2">{h.title}</Typography>
                    <Typography variant="body2">{h.detail}</Typography>
                  </Alert>
                ))}
              </Stack>
            )}
            {collateral?.summary ? (
              <Typography variant="body2" sx={{ mt: 2 }}>
                {String(collateral.summary)}
              </Typography>
            ) : null}
            {collateral?.disclaimer && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                {String(collateral.disclaimer)}
              </Typography>
            )}
            </>
          </div>
        </Card>
      )}

      {tab === 3 && (
        <Card>
          <CardContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Heuristic hints only — not investment advice. Holdings P/L uses live portfolio when you load insights.
            </Alert>
            <Stack spacing={2}>
              {((opportunities?.insights as Array<Record<string, string>>) ?? []).map((ins, idx) => (
                <Paper key={idx} variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="primary">
                    {ins.title}
                  </Typography>
                  <Chip size="small" label={ins.severity} sx={{ my: 0.5 }} />
                  <Typography variant="body2">{ins.detail}</Typography>
                </Paper>
              ))}
              {(!opportunities?.insights || (opportunities.insights as unknown[]).length === 0) && (
                <Typography color="text.secondary">Load opportunities after a successful sync.</Typography>
              )}
            </Stack>
          </CardContent>
        </Card>
      )}
      <BreezeChartAnalysisModal
        open={chartDlg.open}
        onClose={() => setChartDlg({ open: false })}
        initialStockCode={chartDlg.stock}
        initialExchangeLabel={chartDlg.exchange}
      />
    </Box>
  );
};

export default BreezeWealthHub;
